import { useQuery } from '@tanstack/react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import {
  identityVerificationDashboardApi,
  type DedupPolicyTier,
  type IdentityJourneyRow,
  type IdentityJourneyStatusGroup,
  type JourneyStep,
  type JourneyStepStatus,
} from '../api/identityVerificationDashboardApi';
import { messageFromIdentityVerificationHttpError } from '../lib/identityVerificationHttpErrorMessage';

function stepStatusColor(theme: Theme, status: JourneyStepStatus) {
  switch (status) {
    case 'PASS':
      return theme.palette.success.main;
    case 'FAIL':
      return theme.palette.error.main;
    case 'PENDING':
      return theme.palette.warning.main;
    case 'SKIPPED':
      return theme.palette.text.disabled;
    case 'INFO':
      return theme.palette.info.main;
    case 'NOT_RUN':
    default:
      return alpha(theme.palette.text.secondary, 0.5);
  }
}

function stepStatusIcon(status: JourneyStepStatus) {
  switch (status) {
    case 'PASS':
      return <CheckCircleRoundedIcon fontSize="small" />;
    case 'FAIL':
      return <CancelRoundedIcon fontSize="small" />;
    case 'PENDING':
      return <HourglassTopRoundedIcon fontSize="small" />;
    case 'SKIPPED':
      return <RemoveCircleOutlineRoundedIcon fontSize="small" />;
    case 'INFO':
      return <InfoOutlinedIcon fontSize="small" />;
    case 'NOT_RUN':
    default:
      return <HelpOutlineRoundedIcon fontSize="small" />;
  }
}

function stepChipColor(status: JourneyStepStatus): 'success' | 'error' | 'warning' | 'info' | 'default' {
  switch (status) {
    case 'PASS':
      return 'success';
    case 'FAIL':
      return 'error';
    case 'PENDING':
      return 'warning';
    case 'INFO':
      return 'info';
    case 'SKIPPED':
    case 'NOT_RUN':
    default:
      return 'default';
  }
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function tierDetailText(tier: DedupPolicyTier): string {
  const cfg = tier.logic_config ?? {};
  const parts: string[] = [];
  if (typeof cfg.enabled === 'boolean') parts.push(`enabled=${cfg.enabled}`);
  if (typeof cfg.biometric_threshold === 'number') {
    parts.push(`biometric_threshold=${cfg.biometric_threshold}`);
  }
  if (typeof cfg.check_pii === 'boolean') parts.push(`check_pii=${cfg.check_pii}`);
  if (typeof cfg.check_gender === 'boolean') parts.push(`check_gender=${cfg.check_gender}`);
  if (typeof cfg.ai_judge_enabled === 'boolean') parts.push(`ai_judge_enabled=${cfg.ai_judge_enabled}`);
  if (typeof cfg.failure_message === 'string' && cfg.failure_message.trim()) {
    parts.push(`message="${cfg.failure_message.trim()}"`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'No additional config for this tier.';
}

function JourneyStepper({ steps }: { steps: JourneyStep[] }) {
  const theme = useTheme();
  return (
    <Stepper alternativeLabel sx={{ '& .MuiStepConnector-line': { borderColor: 'divider' } }}>
      {steps.map((step) => {
        const color = stepStatusColor(theme, step.status);
        return (
          <Step key={step.key} completed={step.status === 'PASS'} active={step.status !== 'NOT_RUN'}>
            <Tooltip title={step.summary} placement="top" arrow>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: alpha(color, 0.16),
                      color,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `1.5px solid ${alpha(color, 0.6)}`,
                    }}
                  >
                    {stepStatusIcon(step.status)}
                  </Box>
                )}
              >
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {step.label}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    {step.status}
                  </Typography>
                </Box>
              </StepLabel>
            </Tooltip>
          </Step>
        );
      })}
    </Stepper>
  );
}

function JsonBlock({ value }: { value: unknown }) {
  const text = useMemo(() => {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }, [value]);
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1,
        borderRadius: 1.5,
        bgcolor: 'background.default',
        border: 1,
        borderColor: 'divider',
        overflow: 'auto',
        maxHeight: 240,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontSize: '0.74rem',
        lineHeight: 1.45,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {text}
    </Box>
  );
}

const STATUS_GROUPS: Array<{ value: IdentityJourneyStatusGroup | ''; label: string; help: string }> = [
  { value: '', label: 'All sessions', help: 'No filter — every IV session in the page.' },
  { value: 'NO_MITEK', label: 'No Mitek call', help: 'raw_idv_response IS NULL — vendor was never invoked.' },
  { value: 'MITEK_CALLED', label: 'Mitek called', help: 'raw_idv_response IS NOT NULL — vendor responded.' },
  {
    value: 'CUSTOMER_CREATED',
    label: 'Customer created',
    help: 'Sessions whose CCID is set on the IV row (linked to a customers entry).',
  },
  {
    value: 'NO_CUSTOMER',
    label: 'No customer yet',
    help: 'Sessions with no CCID — dedup blocked them or pipeline did not finish.',
  },
];

export function IdentityVerificationJourneys() {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusGroup, setStatusGroup] = useState<IdentityJourneyStatusGroup | ''>('');
  const [tierHit, setTierHit] = useState<string>('');
  const [ivIdQ, setIvIdQ] = useState('');
  const [ccidQ, setCcidQ] = useState('');
  const [emailQ, setEmailQ] = useState('');
  const [applied, setApplied] = useState({
    ivVerificationIdContains: '',
    ccidContains: '',
    emailContains: '',
  });

  const policyQ = useQuery({
    queryKey: ['identityVerificationDedupPolicy'],
    queryFn: () => identityVerificationDashboardApi.policy(),
  });

  const journeysQ = useQuery({
    queryKey: [
      'identityVerificationJourneys',
      page,
      rowsPerPage,
      statusGroup,
      tierHit,
      applied,
    ],
    queryFn: () =>
      identityVerificationDashboardApi.journeys({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        statusGroup: statusGroup || undefined,
        tierHit: tierHit || undefined,
        ivVerificationIdContains: applied.ivVerificationIdContains || undefined,
        ccidContains: applied.ccidContains || undefined,
        emailContains: applied.emailContains || undefined,
      }),
  });

  const applySearch = () => {
    setPage(0);
    setApplied({
      ivVerificationIdContains: ivIdQ.trim(),
      ccidContains: ccidQ.trim(),
      emailContains: emailQ.trim(),
    });
  };

  const tiers = journeysQ.data?.policy?.tiers ?? policyQ.data?.tiers ?? [];

  return (
    <Box>
      <Accordion
        defaultExpanded
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, border: 1, borderColor: 'divider' }}
      >
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ minHeight: 56 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <InfoOutlinedIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Record journey · IDV → Dedup tiers → Mitek → Customer
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 920, lineHeight: 1.6 }}>
            Each row traces one <strong>identity_verification_sessions</strong> record through the pipeline. We mark{' '}
            <em>Deduplication</em> from <code>deduplication_verification_sessions</code>, with tiers pulled from{' '}
            <code>deduplication-policy.json</code> and matched against{' '}
            <code>deduplication_verification_sessions.deduplication_result.matched_tiers</code> to highlight which checks
            actually flagged the candidate. We mark <em>Mitek vendor call</em> as <strong>NOT_RUN</strong> when{' '}
            <code>raw_idv_response</code> is null (returning selfie-only path or pipeline aborted before vendor), and as{' '}
            <strong>PASS / FAIL / INFO</strong> based on the vendor decision when the payload exists. The{' '}
            <strong>Customer</strong> step is green only when a row in{' '}
            <code>customers</code> exists for this session.
          </Typography>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Deduplication policy (threshold {journeysQ.data?.policy?.threshold ?? policyQ.data?.threshold ?? '—'})
              </Typography>
              {policyQ.isError ? (
                <Alert severity="warning">{messageFromIdentityVerificationHttpError(policyQ.error)}</Alert>
              ) : tiers.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  Loading policy…
                </Typography>
              ) : (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {tiers.map((t) => (
                    <Chip
                      key={t.tier}
                      size="small"
                      label={`${t.tier.toUpperCase()} · ${t.label} (w=${t.weight})`}
                      variant={tierHit === t.tier ? 'filled' : 'outlined'}
                      color={tierHit === t.tier ? 'primary' : 'default'}
                      onClick={() => {
                        setPage(0);
                        setTierHit(tierHit === t.tier ? '' : t.tier);
                      }}
                      sx={{ fontWeight: 700, mb: 0.5 }}
                    />
                  ))}
                </Stack>
              )}
              {(journeysQ.data?.policy?.description ?? policyQ.data?.description) ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, lineHeight: 1.5 }}>
                  {journeysQ.data?.policy?.description ?? policyQ.data?.description}
                </Typography>
              ) : null}
              {tiers.length > 0 ? (
                <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                  {tiers.map((t) => (
                    <Box
                      key={`desc-${t.tier}`}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        px: 1,
                        py: 0.75,
                        bgcolor: 'background.default',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 800, display: 'block' }}>
                        {t.tier.toUpperCase()} · {t.label} (weight {t.weight})
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {tierDetailText(t)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : null}
            </CardContent>
          </Card>
        </AccordionDetails>
      </Accordion>

      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status group</InputLabel>
                <Select
                  label="Status group"
                  value={statusGroup}
                  onChange={(e) => {
                    setPage(0);
                    setStatusGroup(e.target.value as IdentityJourneyStatusGroup | '');
                  }}
                >
                  {STATUS_GROUPS.map((g) => (
                    <MenuItem key={g.value || 'any'} value={g.value} title={g.help}>
                      {g.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Dedup tier hit</InputLabel>
                <Select
                  label="Dedup tier hit"
                  value={tierHit}
                  onChange={(e) => {
                    setPage(0);
                    setTierHit(String(e.target.value));
                  }}
                >
                  <MenuItem value="">Any tier (no filter)</MenuItem>
                  {tiers.map((t) => (
                    <MenuItem key={t.tier} value={t.tier}>
                      {t.tier} · {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="iv_verification_id contains"
                value={ivIdQ}
                onChange={(e) => setIvIdQ(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="CCID contains"
                value={ccidQ}
                onChange={(e) => setCcidQ(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Email contains"
                value={emailQ}
                onChange={(e) => setEmailQ(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button size="small" onClick={applySearch}>
                        Apply filters
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {journeysQ.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {messageFromIdentityVerificationHttpError(journeysQ.error)}
        </Alert>
      ) : null}

      {journeysQ.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={36} />
        </Box>
      ) : (journeysQ.data?.items ?? []).length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              No journeys match the current filters.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {(journeysQ.data?.items ?? []).map((row) => (
            <JourneyCard key={row.iv_verification_id} row={row} tiers={tiers} theme={theme} />
          ))}
        </Stack>
      )}

      <TablePagination
        component="div"
        count={journeysQ.data?.total ?? 0}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{ mt: 1 }}
      />
    </Box>
  );
}

function JourneyCard({
  row,
  tiers,
  theme,
}: {
  row: IdentityJourneyRow;
  tiers: { tier: string; label: string; weight: number }[];
  theme: Theme;
}) {
  const [expanded, setExpanded] = useState(false);

  const dedup = row.dedup;
  const tierLookup = useMemo(() => {
    const map = new Map<string, { hit: boolean; evaluated: boolean; details: Record<string, unknown> }>();
    for (const t of dedup?.tier_evaluation ?? []) {
      map.set(t.tier, { hit: t.hit, evaluated: t.evaluated, details: t.details });
    }
    return map;
  }, [dedup?.tier_evaluation]);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.18 : 0.32),
      }}
    >
      <CardContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ md: 'center' }}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
              <Chip
                size="small"
                label={row.session_status}
                color={
                  row.session_status === 'SUCCESS' || row.session_status === 'COMPLETED'
                    ? 'success'
                    : row.session_status === 'REJECTED' || row.session_status === 'FAILED'
                      ? 'error'
                      : 'default'
                }
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={row.verification_route ?? 'no-route'}
                sx={{ fontWeight: 600 }}
              />
              <Chip size="small" variant="outlined" label={`vendor: ${row.vendor}`} sx={{ fontWeight: 600 }} />
              {row.ccid ? (
                <Chip size="small" color="primary" variant="outlined" label={`CCID ${row.ccid}`} sx={{ fontWeight: 700 }} />
              ) : (
                <Chip size="small" variant="outlined" label="no ccid" sx={{ fontWeight: 600 }} />
              )}
            </Stack>
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                fontWeight: 700,
                wordBreak: 'break-all',
              }}
            >
              {row.iv_verification_id}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              internal_id {row.internal_id} · created {formatDateTime(row.created_at)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {row.email ?? 'no email'} · {row.phone_number ?? 'no phone'} ·{' '}
              {row.document_type ?? 'no document'}{' '}
              {[row.issuing_country, row.issuing_province].filter(Boolean).join(' · ') || ''}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
            {row.steps.map((step) => (
              <Tooltip key={step.key} title={step.summary} arrow placement="top">
                <Chip
                  size="small"
                  label={`${step.label}: ${step.status}`}
                  color={stepChipColor(step.status)}
                  variant={step.status === 'NOT_RUN' || step.status === 'SKIPPED' ? 'outlined' : 'filled'}
                  sx={{ fontWeight: 700 }}
                  icon={stepStatusIcon(step.status)}
                />
              </Tooltip>
            ))}
            <IconButton
              size="small"
              onClick={() => setExpanded((v) => !v)}
              aria-label={expanded ? 'Collapse journey' : 'Expand journey'}
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            >
              <ExpandMoreRoundedIcon />
            </IconButton>
          </Stack>
        </Stack>

        <Box sx={{ mt: 2 }}>
          <JourneyStepper steps={row.steps} />
        </Box>

        {expanded ? (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Dedup tier matrix (from deduplication-policy.json)
                    </Typography>
                    {tiers.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        Policy not loaded.
                      </Typography>
                    ) : (
                      <Stack spacing={0.85}>
                        {tiers.map((t) => {
                          const evaluated = tierLookup.get(t.tier);
                          const evaluatedNow = !!evaluated?.evaluated;
                          const hit = !!evaluated?.hit;
                          const status: JourneyStepStatus = !evaluatedNow
                            ? 'NOT_RUN'
                            : hit
                              ? 'FAIL'
                              : 'PASS';
                          return (
                            <Stack key={t.tier} direction="row" spacing={1} alignItems="center">
                              <Chip
                                size="small"
                                label={status}
                                color={stepChipColor(status)}
                                variant={status === 'NOT_RUN' ? 'outlined' : 'filled'}
                                sx={{ fontWeight: 700, minWidth: 76 }}
                                icon={stepStatusIcon(status)}
                              />
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {t.tier.toUpperCase()} · {t.label}{' '}
                                  <Box component="span" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                    (weight {t.weight})
                                  </Box>
                                </Typography>
                                {evaluatedNow && evaluated?.details && Object.keys(evaluated.details).length ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', wordBreak: 'break-word' }}
                                  >
                                    {Object.entries(evaluated.details)
                                      .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
                                      .join(' · ')}
                                  </Typography>
                                ) : !evaluatedNow ? (
                                  <Typography variant="caption" color="text.secondary">
                                    Tier not evaluated for this session.
                                  </Typography>
                                ) : null}
                              </Box>
                            </Stack>
                          );
                        })}
                      </Stack>
                    )}
                    {dedup ? (
                      <Box
                        sx={{
                          mt: 1.5,
                          p: 1,
                          borderRadius: 1.5,
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                          border: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          dv_verification_id <strong>{dedup.dv_verification_id}</strong> · status{' '}
                          <strong>{dedup.status}</strong>
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                          score{' '}
                          <strong>
                            {dedup.score ?? '—'} / {dedup.threshold ?? '—'}
                          </strong>{' '}
                          · isDuplicate <strong>{String(dedup.is_duplicate)}</strong> · matched_tiers{' '}
                          <strong>{dedup.matched_tiers.join(', ') || 'none'}</strong>
                        </Typography>
                        {dedup.message ? (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            {dedup.message}
                          </Typography>
                        ) : null}
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ mt: 1.5 }}>
                        No row in <code>deduplication_verification_sessions</code> for this session yet.
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Per-step details
                    </Typography>
                    <Stack spacing={1}>
                      {row.steps.map((step) => (
                        <Box
                          key={step.key}
                          sx={{
                            p: 1,
                            borderRadius: 1.5,
                            border: 1,
                            borderColor: alpha(stepStatusColor(theme, step.status), 0.4),
                            bgcolor: alpha(stepStatusColor(theme, step.status), 0.05),
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <Chip
                              size="small"
                              label={step.status}
                              color={stepChipColor(step.status)}
                              variant={step.status === 'NOT_RUN' || step.status === 'SKIPPED' ? 'outlined' : 'filled'}
                              icon={stepStatusIcon(step.status)}
                              sx={{ fontWeight: 700 }}
                            />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {step.label}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                            {step.summary}
                          </Typography>
                          {step.details && Object.keys(step.details).length > 0 ? (
                            <Box sx={{ mt: 0.85 }}>
                              <JsonBlock value={step.details} />
                            </Box>
                          ) : null}
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
                {row.customer ? (
                  <Card variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Linked customer (CCID {row.customer.ccid})
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">
                            Name
                          </Typography>
                          <Typography variant="body2">
                            {[row.customer.first_name, row.customer.last_name].filter(Boolean).join(' ') || '—'}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">
                            Status
                          </Typography>
                          <Typography variant="body2">{row.customer.status}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">
                            Email
                          </Typography>
                          <Typography variant="body2">{row.customer.email ?? '—'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography variant="body2">{row.customer.phone_number ?? '—'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="caption" color="text.secondary">
                            Address
                          </Typography>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {row.customer.residential_address ?? '—'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ) : null}
              </Grid>
            </Grid>
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
}
